import { useState, useEffect } from 'react';
import { Sparkles, X, Loader2, Send, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import { generateV2, formatGenerationError } from '../lib/generationClient';
import { useRetryCountdown } from '../lib/useRetryCountdown';
import ConfirmDialog from './admin/ConfirmDialog';

interface PromptPopupProps {
  isOpen: boolean;
  onClose: () => void;
  /** Der reine Section-Prompt (NICHT der Master Prompt, OHNE Kontext!) */
  initialPrompt: string;
  /** Kontext-Text für die Anzeige (wird separat angezeigt, nicht editierbar) */
  contextPreview?: string;
  sectionKey: string;
  cardId: string;
  darkMode: boolean;
  onGenerated: (text: string) => void;
  /** Formulardaten – werden separat ans Backend geschickt */
  formData?: Record<string, string>;
  /** Kundendaten – werden separat ans Backend geschickt */
  customerData?: {
    name?: string;
    branche?: string;
    rechtsform?: string;
    ort?: string;
  };
  /** Validierungswarnungen */
  warnings?: string[];
  /** Kunden-ID für Website-Analyse-Kontext */
  customerId?: number;
  /** Ist der aktuelle User ein Admin? */
  isAdmin?: boolean;
}

export function PromptPopup({
  isOpen,
  onClose,
  initialPrompt,
  contextPreview,
  sectionKey,
  cardId,
  darkMode,
  onGenerated,
  formData,
  customerData,
  warnings: externalWarnings,
  customerId,
  isAdmin,
}: PromptPopupProps) {
  // NUR den Section-Prompt editierbar halten (OHNE Kontext!)
  const storageKey = `section-prompt-${cardId}`;
  const sectionLabels: Record<string, string> = {
    'analysis': 'Abschnitt 2 – Analyse',
    'execution': 'Abschnitt 3 – Beratungsverlauf',
    'weaknesses': 'Abschnitt 4 – Schwachstellen',
    'action-plan': 'Abschnitt 5 – Maßnahmenplan',
    '1-1': 'Abschnitt 1.1',
    '1-2': 'Abschnitt 1.2',
    '1-3': 'Abschnitt 1.3',
    '1-4': 'Abschnitt 1.4',
    '1-5': 'Abschnitt 1.5',
  };
  const sectionLabel = sectionLabels[cardId] || cardId;
  const [sectionPrompt, setSectionPrompt] = useState(String(initialPrompt || ''));
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Retry-After-Sekunden fuer RATE_LIMITED — loest einen sichtbaren Countdown aus.
  const [retrySeconds, setRetrySeconds] = useState<number | null>(null);
  const remainingRetry = useRetryCountdown(retrySeconds);
  const [selectedModel, setSelectedModel] = useState<'sonnet' | 'opus'>('opus');
  const [promptSource, setPromptSource] = useState<'standard' | 'lokal' | 'bearbeitet'>('standard');
  // Backend-Sicherheitswarnungen (z.B. bei auffälligen Eingaben)
  const [backendWarnings, setBackendWarnings] = useState<string[]>([]);
  const [resetConfirm, setResetConfirm] = useState(false);

  const performReset = async () => {
    setResetConfirm(false);
    localStorage.removeItem(storageKey);
    try {
      await api.delete(`/api/prompts/reset?cardId=${cardId}`);
      console.log('[PromptPopup] Gespeicherter Prompt für', cardId, 'zurückgesetzt');
    } catch (err) {
      console.warn('[PromptPopup] DB-Reset fehlgeschlagen:', err);
    }
    try {
      const res = await api.get<{ promptText: string }>(`/api/prompts/default?cardId=${cardId}`);
      const backendDefault = (res as any)?.promptText || '';
      if (backendDefault) {
        setSectionPrompt(backendDefault);
        setPromptSource('standard');
        return;
      }
    } catch (err) {
      console.warn('[PromptPopup] Backend-Default nicht erreichbar, nutze initialPrompt');
    }
    setSectionPrompt(String(initialPrompt || ''));
    setPromptSource('standard');
  };

  useEffect(() => {
    // Gespeicherten Prompt laden (falls vorhanden), sonst Standard
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      setSectionPrompt(saved);
      setPromptSource('lokal');
    } else {
      setSectionPrompt(String(initialPrompt || ''));
      setPromptSource('standard');
    }
    setError(null);
  }, [initialPrompt, isOpen]);

  const handlePromptChange = (value: string) => {
    setSectionPrompt(value);
    setPromptSource('bearbeitet');
  };

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setIsGenerating(true);
    setError(null);
    setRetrySeconds(null);
    setBackendWarnings([]);
    // Beim Abschicken den bearbeiteten Prompt speichern (nur wenn vom Default abweichend)
    if (promptSource === 'bearbeitet') {
      try { localStorage.setItem(storageKey, sectionPrompt); } catch {}
    }

    try {
      const result = await generateV2({
        cardId,
        // sectionPrompt nur bei Admin mitsenden: Backend akzeptiert das Feld
        // ausschließlich für Admin-JWTs. Non-admin-Fall sollte dieses Popup
        // gar nicht öffnen — defensive Absicherung.
        ...(isAdmin ? { sectionPrompt } : {}),
        formData: formData || {},
        customerData: customerData || {},
        customerId: customerId || undefined,
        model: selectedModel,
      });

      if (!result.ok) {
        // Strukturierte Fehler: 409 ALREADY_RUNNING, 429 RATE_LIMITED, Sicherheits-Block, Netzwerk.
        // Alle bekommen eine einheitliche deutsche Meldung.
        setError(formatGenerationError(result.error));
        if (
          (result.error.code === 'RATE_LIMITED' || result.error.code === 'BLOCKED') &&
          result.error.retryAfterSeconds
        ) {
          setRetrySeconds(result.error.retryAfterSeconds);
        }
        if (result.error.warnings && result.error.warnings.length > 0) {
          setBackendWarnings(result.error.warnings);
        }
        return; // Popup bleibt offen, Nutzer kann reagieren
      }

      if (result.warnings && result.warnings.length > 0) {
        console.warn('[PROMPT] Backend-Warnungen:', result.warnings);
        setBackendWarnings(result.warnings);
      }

      onGenerated(result.data.text);
      onClose();
    } finally {
      setIsGenerating(false);
    }
  };

  const warnings = externalWarnings || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={!isGenerating ? onClose : undefined}
      />

      <div
        className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-xl shadow-2xl ${
          darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
        } border`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 sm:p-6 border-b ${
            darkMode ? 'border-slate-700' : 'border-slate-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Soll dieses Kommando an die Claude-CLI geschickt werden?
              </h2>
              <div className={`flex items-center gap-2 text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {isAdmin ? (
                  <>
                    <span>Modell:</span>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="model-select"
                        value="sonnet"
                        checked={selectedModel === 'sonnet'}
                        onChange={() => setSelectedModel('sonnet')}
                        className="accent-blue-500"
                      />
                      <span>Sonnet</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="model-select"
                        value="opus"
                        checked={selectedModel === 'opus'}
                        onChange={() => setSelectedModel('opus')}
                        className="accent-blue-500"
                      />
                      <span>Opus</span>
                    </label>
                    <span>· Lokale CLI · Master-Prompt wird automatisch vorgeschaltet</span>
                  </>
                ) : (
                  <span>Modell: sonnet · Lokale CLI · Master-Prompt wird automatisch vorgeschaltet</span>
                )}
              </div>
              <div className={`flex items-center gap-2 text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <span>Prompt-Version:</span>
                <span className={`px-1.5 py-0.5 rounded font-medium ${
                  promptSource === 'standard'
                    ? darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                    : promptSource === 'lokal'
                      ? darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                      : darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
                }`}>
                  {promptSource === 'standard' ? 'Standard (Backend)' : promptSource === 'lokal' ? 'Lokal gespeichert' : 'Bearbeitet'}
                </span>
                <span>· {sectionPrompt.length} Z. · {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })} {new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isGenerating}
            className={`p-2 rounded-lg transition-colors ${
              darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
            } disabled:opacity-50`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {/* Warnungen */}
          {warnings.length > 0 && (
            <div className={`p-3 rounded-lg flex items-start gap-2 border ${
              darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'
            }`}>
              <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <div>
                <p className={`text-sm font-medium ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                  Fehlende Angaben:
                </p>
                <ul className={`text-xs mt-1 space-y-0.5 ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  {warnings.map((w, i) => <li key={i}>• {w}</li>)}
                </ul>
              </div>
            </div>
          )}

          {/* Editierbarer Section-Prompt */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              Abschnitts-Prompt (bearbeitbar)
            </label>
            <textarea
              value={sectionPrompt}
              onChange={(e) => handlePromptChange(e.target.value)}
              disabled={isGenerating}
              rows={12}
              className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y font-mono leading-relaxed ${
                darkMode
                  ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                  : 'bg-slate-50 border-slate-300 text-slate-900'
              } disabled:opacity-60`}
            />
          </div>

          {/* Kontext-Vorschau (nur Anzeige, nicht editierbar) */}
          {contextPreview && (
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Kontext (wird automatisch angehängt)
              </label>
              <div className={`px-4 py-3 rounded-lg text-xs font-mono leading-relaxed whitespace-pre-wrap ${
                darkMode
                  ? 'bg-slate-900/50 border-slate-700 text-slate-400'
                  : 'bg-slate-100 border-slate-200 text-slate-500'
              } border`}>
                {contextPreview}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              {remainingRetry !== null && remainingRetry > 0 && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Nächster Versuch möglich in {remainingRetry} Sekunden.
                </p>
              )}
            </div>
          )}

          {/* Backend-Sicherheitswarnungen (Yellow/Red vom API) */}
          {backendWarnings.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
                Sicherheitshinweis
              </p>
              <ul className="text-sm text-amber-700 dark:text-amber-300 list-disc pl-5 space-y-1">
                {backendWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Generating indicator */}
          {isGenerating && (
            <div className={`p-3 rounded-lg flex items-center gap-3 ${
              darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
            } border`}>
              <Loader2 className={`w-4 h-4 animate-spin ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                Claude generiert Text... Dies kann bis zu 2 Minuten dauern.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between gap-3 p-4 sm:p-6 border-t ${
          darkMode ? 'border-slate-700' : 'border-slate-200'
        }`}>
          <button
            type="button"
            onClick={() => setResetConfirm(true)}
            disabled={isGenerating}
            className={`text-xs px-3 py-1.5 rounded transition-colors ${
              darkMode
                ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-700'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            } disabled:opacity-50`}
          >
            ↺ Prompt zurücksetzen
          </button>
          <button
            onClick={handleSubmit}
            disabled={isGenerating || !sectionPrompt.trim()}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="inline-flex">
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </span>
            <span>{isGenerating ? 'Wird generiert...' : 'Abschicken'}</span>
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={resetConfirm}
        title="Prompt zurücksetzen"
        message={`Prompt für ${sectionLabel} auf Standard zurücksetzen? Deine Änderungen gehen verloren.`}
        confirmLabel="Zurücksetzen"
        onConfirm={performReset}
        onCancel={() => setResetConfirm(false)}
      />
    </div>
  );
}
